import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Clock, Users, MapPin, Loader2, ChefHat, ImagePlus } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { RECIPE_IMAGES } from "@/data/recipes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Recipes() {
  const { t } = useLanguage();
  const r = t.recipes;
  const L = r.labels;

  const [community, setCommunity] = useState([]);
  const [form, setForm] = useState({ submitter: "", title: "", city: "", country: "", description: "", ingredients: "", method: "", image_url: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadCommunity = useCallback(async () => {
    try {
      const res = await fetch(`${API}/recipes/community`);
      const data = await res.json();
      setCommunity(Array.isArray(data) ? data : []);
    } catch {
      setCommunity([]);
    }
  }, []);

  useEffect(() => {
    loadCommunity();
  }, [loadCommunity]);

  const submitRecipe = async (e) => {
    e.preventDefault();
    if (!form.submitter.trim() || !form.title.trim() || !form.ingredients.trim() || !form.method.trim()) {
      toast.error("Please add your name, a recipe title, ingredients and the method.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/recipes/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Thank you — your family recipe has been submitted for approval by the clan gathering.");
      setForm({ submitter: "", title: "", city: "", country: "", description: "", ingredients: "", method: "", image_url: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Downscale + compress an uploaded dish photo into a small JPEG data URL.
  const handleUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
        if (dataUrl.length > 900000) {
          toast.error("That image is too large — please try a smaller one or paste a link.");
          return;
        }
        setForm((f) => ({ ...f, image_url: dataUrl }));
        toast.success("Photo added.");
      };
      img.onerror = () => toast.error("Could not read that image.");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const field =
    "w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-4 py-3 font-sans text-clan-ink placeholder:text-clan-muted/70 transition-colors duration-300 focus:border-clan-accent focus:bg-clan-ink/[0.06] focus:outline-none focus:ring-1 focus:ring-clan-accent";

  return (
    <div className="relative min-h-screen bg-clan-bg text-clan-ink">
      <div className="grain-overlay" aria-hidden="true" />
      <Toaster position="top-center" theme="dark" />
      <Header />

      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 lg:px-10 lg:pt-40">
        {/* Hero */}
        <motion.div
          data-testid="recipes-hero"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-clan-accent">
            {r.hero.overline}
          </span>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.02] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
            {r.hero.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-clan-muted">{r.hero.subtitle}</p>
        </motion.div>

        {/* Recipes */}
        <div className="mt-16 flex flex-col gap-20 lg:gap-28">
          {r.items.map((item, idx) => (
            <motion.article
              key={item.id}
              data-testid={`recipe-card-${item.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className={`flex flex-col gap-8 lg:items-stretch lg:gap-12 ${
                idx % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
              }`}
            >
              {/* Image */}
              <div className="relative w-full overflow-hidden rounded-lg lg:w-[45%]">
                <span className="absolute left-4 top-4 z-10 font-serif text-5xl text-white/90 drop-shadow-lg sm:text-6xl">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <img
                  src={RECIPE_IMAGES[item.id]}
                  alt={item.name}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-transform duration-[1.2s] ease-out hover:scale-105 sm:h-80 lg:h-full lg:min-h-[24rem]"
                />
              </div>

              {/* Content */}
              <div className="flex w-full flex-col lg:w-[55%]">
                <h2 className="font-serif text-3xl font-light leading-tight text-clan-ink sm:text-4xl">
                  {item.name}
                </h2>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.14em] text-clan-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-clan-accent" /> {item.origin}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-clan-accent" /> {L.serves} {item.serves}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-clan-accent" /> {item.time}
                  </span>
                </div>

                <p className="mt-5 text-base italic leading-relaxed text-clan-muted">{item.desc}</p>

                <div className="mt-7 grid gap-8 sm:grid-cols-2">
                  {/* Ingredients */}
                  <div>
                    <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-clan-accent">
                      {L.ingredients}
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {item.ingredients.map((ing, i) => (
                        <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-clan-ink/80">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-clan-accent" />
                          {ing}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Method */}
                  <div>
                    <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-clan-accent">
                      {L.method}
                    </h3>
                    <ol className="flex flex-col gap-3">
                      {item.steps.map((step, i) => (
                        <li key={i} className="flex gap-3 text-sm leading-relaxed text-clan-ink/80">
                          <span className="font-serif text-lg leading-none text-clan-accent/70">{i + 1}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Community recipes (from the clan) */}
        <section className="mt-24 border-t border-clan-ink/10 pt-16">
          <span className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-clan-accent">
            From the Clan Table
          </span>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight text-clan-ink sm:text-4xl">
            Family recipes, shared by the clan
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-clan-muted">
            Recipes handed down through McDonald kitchens the world over. Every submission is reviewed before it appears here.
          </p>

          {community.length > 0 && (
            <div data-testid="community-recipes" className="mt-10 grid gap-6 sm:grid-cols-2">
              {community.map((c) => (
                <motion.article
                  key={c.id}
                  data-testid={`community-recipe-${c.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5 }}
                  className="overflow-hidden rounded-lg border border-clan-ink/10 bg-clan-surface"
                >
                  {c.image_url && (
                    <img src={c.image_url} alt={c.title} loading="lazy" className="h-44 w-full object-cover" />
                  )}
                  <div className="p-6">
                  <div className="flex items-center gap-2 text-clan-accent">
                    <ChefHat className="h-4 w-4" />
                    <h3 className="font-serif text-xl text-clan-ink">{c.title}</h3>
                  </div>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-clan-muted">
                    {c.submitter}
                    {(c.city || c.country) && ` · ${[c.city, c.country].filter(Boolean).join(", ")}`}
                  </p>
                  {c.description && <p className="mt-3 text-sm italic leading-relaxed text-clan-muted">{c.description}</p>}
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clan-accent">{L.ingredients}</h4>
                      <p className="whitespace-pre-line text-sm leading-relaxed text-clan-ink/80">{c.ingredients}</p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clan-accent">{L.method}</h4>
                      <p className="whitespace-pre-line text-sm leading-relaxed text-clan-ink/80">{c.method}</p>
                    </div>
                  </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          {/* Suggest a recipe */}
          <form
            onSubmit={submitRecipe}
            data-testid="recipe-submit-form"
            className="mt-10 rounded-lg border border-clan-ink/10 bg-clan-surface p-6 sm:p-8"
          >
            <h3 className="font-serif text-2xl text-clan-ink">Suggest a recipe</h3>
            <p className="mb-6 mt-1 text-sm text-clan-muted">Share a family dish — it will appear here once approved.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="recipe-submitter-input" value={form.submitter} onChange={(e) => setForm({ ...form, submitter: e.target.value })} placeholder="Your name" className={field} />
              <input data-testid="recipe-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Recipe title" className={field} />
              <input data-testid="recipe-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City (optional)" className={field} />
              <input data-testid="recipe-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country (optional)" className={field} />
            </div>
            <textarea data-testid="recipe-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="A short description (optional)" rows={2} className={`${field} mt-3`} />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <textarea data-testid="recipe-ingredients-input" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} placeholder="Ingredients (one per line)" rows={5} className={field} />
              <textarea data-testid="recipe-method-input" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} placeholder="Method (step by step)" rows={5} className={field} />
            </div>

            {/* Dish photo: link or upload */}
            <input
              data-testid="recipe-image-url-input"
              value={form.image_url.startsWith("data:") ? "" : form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="Dish photo link (optional)"
              className={`${field} mt-3`}
            />
            <div className="mt-3 flex items-center gap-3">
              <label
                data-testid="recipe-image-upload-label"
                className="flex cursor-pointer items-center gap-2 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:border-clan-accent/60 hover:text-clan-ink"
              >
                <ImagePlus className="h-3.5 w-3.5" /> Upload dish photo
                <input
                  data-testid="recipe-image-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
              </label>
              {form.image_url && (
                <div className="flex items-center gap-2">
                  <img src={form.image_url} alt="preview" className="h-10 w-10 rounded object-cover" />
                  <button
                    type="button"
                    data-testid="recipe-image-clear-btn"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="font-mono text-[10px] uppercase tracking-widest text-clan-muted hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <button
              data-testid="recipe-submit-btn"
              type="submit"
              disabled={submitting}
              className="mt-4 flex items-center justify-center gap-2 rounded-md bg-clan-primary px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Submitting…" : "Submit recipe"}
            </button>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
}
